import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Input, Space, Select, DatePicker } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  SlackOutlined,
} from '@ant-design/icons'
import axiosInstance from '../../Utils/axiosInstance'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import {
  GetCustomerDataAPI,
  GetPaymentReceiptDataAPI,
  CreatePaymentReceiptDataAPI,
  UpdatePaymentReceiptDataAPI,
  DeletePaymentReceiptDataAPI,
  GetSaleEntryDataOfCustomer,
  BaseIp,
  ViewPaymentReceiptPdf,
} from '../../AllAPI/AdminAPI'

const PaymentReceipt = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))
  const [receipts, setReceipts] = useState([])
  const [customers, setCustomers] = useState([])
  const [filteredSales, setFilteredSales] = useState([])

  const [tableLoading, setTableLoading] = useState(false)
  const [customerLoading, setCustomerLoading] = useState(false)
  const [saleLoading, setSaleLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [pdfLoadingId, setPdfLoadingId] = useState(null)

  const [searchText, setSearchText] = useState('')

  /* ================= Modal ================= */
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  /* ================= Inputs ================= */
  const [date, setDate] = useState(null)
  const [customerId, setCustomerId] = useState(null)
  const [saleIds, setSaleIds] = useState([])
  const [billAmount, setBillAmount] = useState(0)
  const [receivedAmount, setReceivedAmount] = useState('')
  const [paymentType, setPaymentType] = useState(null)

  /* ================= Extra Payment Fields ================= */
  const [upiId, setUpiId] = useState('')
  const [bankRef, setBankRef] = useState('')
  const [cardRef, setCardRef] = useState('')

  /* ================= Delete ================= */
  const [deleteId, setDeleteId] = useState(null)

  useEffect(() => {
    fetchCustomers()
    fetchReceipts()
  }, [])

  /* ================= Fetch ================= */
  const fetchReceipts = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(
        `${GetPaymentReceiptDataAPI}/${selectedYear}`,
      )
      setReceipts(Array.isArray(res.data) ? res.data : [])
    } finally {
      setTableLoading(false)
    }
  }

  const fetchCustomers = async () => {
    try {
      setCustomerLoading(true)
      const res = await axiosInstance.get(
        `${GetCustomerDataAPI}/${selectedYear}`,
      )
      setCustomers(Array.isArray(res.data) ? res.data : [])
    } finally {
      setCustomerLoading(false)
    }
  }

  /* ================= Customer Change ================= */
  const handleCustomerChange = async (custId) => {
    setCustomerId(custId)
    setSaleIds([])
    setBillAmount(0)
    setReceivedAmount('')
    setFilteredSales([])

    try {
      setSaleLoading(true)

      const res = await axiosInstance.get(
        `${GetSaleEntryDataOfCustomer}/${custId}`,
      )

      setFilteredSales(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load sales')
    } finally {
      setSaleLoading(false)
    }
  }

  /* ================= Sale Change ================= */
  // const handleSaleChange = (selectedIds) => {
  //   setSaleIds(selectedIds)

  //   const totalBill = filteredSales
  //     .filter((s) => selectedIds.includes(s.id))
  //     .reduce(
  //       (sum, s) => sum + Number(s.gross_total_amount || 0),
  //       0,
  //     )

  //   setBillAmount(totalBill)
  //   setReceivedAmount(totalBill)
  // }

  const handleSaleChange = (selectedIds) => {
    setSaleIds(selectedIds)

    const totalBill = filteredSales
      .filter((s) => selectedIds.includes(s.sales_id))
      .reduce((sum, s) => {
        const grossTotal = Number(s.gross_total || 0)
        const loading = Number(s.loading_charges || 0)
        const discount = Number(s.discount_amount || 0)

        return sum + grossTotal + loading - discount
      }, 0)

    setBillAmount(totalBill)
    setReceivedAmount(totalBill)
  }

  /* ================= Payment Type ================= */
  const handlePaymentTypeChange = (value) => {
    setPaymentType(value)
    setUpiId('')
    setBankRef('')
    setCardRef('')
  }

  /* ================= Open Modal ================= */
  const openModal = (record = null) => {
    if (record) {
      setEditingId(record.id)
      setDate(dayjs(record.date))
      setCustomerId(record.customer_id)
      setSaleIds(record.sale_ids || [])
      setBillAmount(record.bill_amount)
      setReceivedAmount(record.received_amount)
      setPaymentType(record.payment_type)
      setUpiId(record.upi_id || '')
      setBankRef(record.bank_ref || '')
      setCardRef(record.card_ref || '')
    } else {
      setEditingId(null)
      setDate(null)
      setCustomerId(null)
      setSaleIds([])
      setBillAmount(0)
      setReceivedAmount('')
      setPaymentType(null)
      setUpiId('')
      setBankRef('')
      setCardRef('')
      setFilteredSales([])
    }
    setIsModalOpen(true)
  }

  const PAYMENT_TYPE_MAP = {
    Cash: 1,
    UPI: 2,
    Card: 3,
    Bank: 4,
  }

  /* ================= Save / Update ================= */
  const handleSave = async () => {
    if (!date) return toast.error('Date is required')
    if (!customerId) return toast.error('Customer is required')
    if (!saleIds.length) return toast.error('Select sale numbers')
    if (!receivedAmount || receivedAmount <= 0)
      return toast.error('Enter received amount')
    if (!paymentType) return toast.error('Payment type is required')

    const selectedCustomer = customers.find((c) => c.id === customerId)

    const customerName = selectedCustomer?.customer_name || ''
    const customerNumber = selectedCustomer?.contact || ''
    const customerCity = selectedCustomer?.city?.city_name || ''

    const items = filteredSales
      .filter((s) => saleIds.includes(s.sales_id))
      .map((s) => ({
        sales_id: s.sales_id,
        sales_date: dayjs(s.sales_date).format('YYYY-MM-DD'),
      }))

    const payload = {
      date: date.format('YYYY-MM-DD'),
      cust_id: customerId,
      customer_name: customerName,
      customer_num: customerNumber,
      customer_city: customerCity,
      bill_amount: billAmount,
      year: selectedYear,
      received_amount: Number(receivedAmount),
      payment_type: PAYMENT_TYPE_MAP[paymentType],
      payment_details: upiId || bankRef || cardRef,
      items: items,
    }

    try {
      setSaveLoading(true)
      const res = await axiosInstance.post(CreatePaymentReceiptDataAPI, payload)

      if (res.data?.message === 'SAVED') {
        toast.success('Payment receipt added')

        if (res.data.pdf) {
          const pdfPath = res.data.pdf.replace(/\\/g, '/')
          const pdfUrl = `${BaseIp}${pdfPath}`

          window.open(pdfUrl, '_blank')
        }

        fetchReceipts()
        setIsModalOpen(false)
      } else {
        toast.error('Save failed')
      }
    } catch {
      toast.error('Failed to save payment receipt')
    } finally {
      setSaveLoading(false)
    }
  }

  /* ================= Search ================= */
  const filteredReceipts = receipts.filter((r) =>
    r.customer_name?.toLowerCase().includes(searchText.toLowerCase()),
  )

  // =========Open Invoice PDF===========
  // const handleOpenPdf = async (record) => {
  //   try {
  //     const res = await axiosInstance.get(
  //       `${ViewPaymentReceiptPdf}/${record.receipt_id}/${record.cust_id}`,
  //     )

  //     if (res.data?.payReceiptPath) {
  //       const pdfPath = res.data.payReceiptPath.replace(/\\/g, '/')
  //       const pdfUrl = `${BaseIp}${pdfPath}`

  //       window.open(pdfUrl, '_blank')
  //     } else {
  //       toast.error('PDF not found')
  //     }
  //   } catch (err) {
  //     toast.error('Failed to load PDF')
  //   }
  // }
  const handleOpenPdf = async (record) => {
    try {
      setPdfLoadingId(record.receipt_id)

      const res = await axiosInstance.get(
        `${ViewPaymentReceiptPdf}/${record.receipt_id}/${record.cust_id}`,
      )

      if (res.data?.payReceiptPath) {
        const pdfPath = res.data.payReceiptPath.replace(/\\/g, '/')
        const pdfUrl = `${BaseIp}${pdfPath}`
        window.open(pdfUrl, '_blank')
      } else {
        toast.error('PDF not found')
      }
    } catch {
      toast.error('Failed to load PDF')
    } finally {
      setPdfLoadingId(null)
    }
  }

  /* ================= Table ================= */
  const columns = [
    {
      title: 'Sr No',
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Date',
      dataIndex: 'receipt_date',
      render: (d) => dayjs(d).format('DD-MM-YYYY'),
    },
    { title: 'Customer', dataIndex: 'customer_name' },
    {
      title: 'Bill Amount (₹)',
      dataIndex: 'bill_amount',
      render: (value) => (
        <div style={{ textAlign: 'right' }}> {Number(value)}</div>
      ),
    },
    {
      title: 'Received Amount (₹)',
      dataIndex: 'received_amount',
      render: (value) => (
        <div style={{ textAlign: 'right' }}> {Number(value)}</div>
      ),
    },
    {
      title: 'Payment Type',
      dataIndex: 'payment_type',
      render: (val) => {
        if (val === 1) return 'Cash'
        if (val === 2) return 'UPI'
        if (val === 3) return 'Card'
        if (val === 4) return 'Bank'
        return '-'
      },
    },
    {
      title: 'View Receipt',
      render: (_, record) => (
        <Space>
          <Button
            icon={<FilePdfOutlined />}
            size="small"
            title="Pdf"
            type="primary"
            danger
            loading={pdfLoadingId === record.receipt_id}
            onClick={() => handleOpenPdf(record)}
          />
        </Space>
      ),
    },
  ]

  return (
    <>
      <div className="mainContainer">
        <div className="header-raw">
          <h3>
            <SlackOutlined style={{ color: 'orangered' }} /> Payment Receipt
          </h3>
        </div>

        <div className="search-bar text-end">
          <Button
            icon={<PlusOutlined />}
            className="addButton mx-md-2"
            onClick={() => openModal()}
          >
            Add
          </Button>

          <Input
            placeholder="Search by customer..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredReceipts}
          rowKey="id"
          loading={tableLoading}
          bordered
        />
      </div>

      {/* ================= MODAL ================= */}
      <Modal
        title={editingId ? 'Edit Receipt' : 'Add Receipt'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saveLoading}
        okText={editingId ? 'Update' : 'Save'}
      >
        <label>Date</label>
        <DatePicker value={date} onChange={setDate} style={{ width: '100%' }} />

        <label className="mt-2">Customer</label>
        <Select
          loading={customerLoading}
          value={customerId}
          onChange={handleCustomerChange}
          placeholder="Select customer"
          style={{ width: '100%' }}
        >
          {customers.map((c) => (
            <Select.Option key={c.id} value={c.id}>
              {c.customer_name}
            </Select.Option>
          ))}
        </Select>

        <label className="mt-2">Sales Entry</label>

        <Select
          mode="multiple"
          loading={saleLoading}
          value={saleIds}
          onChange={handleSaleChange}
          placeholder="Select sale numbers"
          style={{ width: '100%' }}
        >
          {filteredSales.map((s) => {
            const netTotal =
              (s.gross_total || 0) -
              (s.discount_amount || 0) +
              (s.loading_charges || 0)

            return (
              <Select.Option key={s.sales_id} value={s.sales_id}>
                <b>Sales Id :</b> {s.sales_id} &nbsp; | &nbsp;
                <b>Date :</b> {dayjs(s.sales_date).format('DD-MM-YYYY')} &nbsp;
                | &nbsp;
                <b>Net Total :</b> ₹{netTotal}
              </Select.Option>
            )
          })}
        </Select>

        <label className="mt-2">Bill Amount</label>
        <Input value={billAmount} disabled />

        <label className="mt-2">Received Amount</label>
        <Input
          type="number"
          value={receivedAmount}
          onChange={(e) => setReceivedAmount(e.target.value)}
        />

        <label className="mt-2">Payment Type</label>
        <Select
          value={paymentType}
          onChange={handlePaymentTypeChange}
          placeholder="Select payment type"
          style={{ width: '100%' }}
        >
          <Select.Option value="Cash">Cash</Select.Option>
          <Select.Option value="UPI">UPI</Select.Option>
          <Select.Option value="Card">Card</Select.Option>
          <Select.Option value="Bank">Bank</Select.Option>
        </Select>

        {paymentType === 'UPI' && (
          <>
            <label className="mt-2">UPI ID</label>
            <Input value={upiId} onChange={(e) => setUpiId(e.target.value)} />
          </>
        )}

        {paymentType === 'Bank' && (
          <>
            <label className="mt-2">Bank Reference</label>
            <Input
              value={bankRef}
              onChange={(e) => setBankRef(e.target.value)}
            />
          </>
        )}

        {paymentType === 'Card' && (
          <>
            <label className="mt-2">Card Transaction ID</label>
            <Input
              value={cardRef}
              onChange={(e) => setCardRef(e.target.value)}
            />
          </>
        )}
      </Modal>
    </>
  )
}

export default PaymentReceipt
